import ErrorHandler from '@/components/Error/ErrorHandler'
import FaturasView from '@/components/Fatura/FaturasView'
import SaldoAntecipadoResumoCard from '@/components/Fatura/SaldoAntecipadoResumoCard'
import Loading from '@/components/Loading'
import useAuth from '@/contexts/useAuth'
import { getFaturasAbertas } from '@/services/fatura'
import { getSaldoAntecipado } from '@/services/saldo-antecipado'
import type { Fatura } from '@/types/fatura'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function Faturas() {
    const { t } = useTranslation()
    const { empresaURL } = useParams<{ empresaURL: string }>()
    const { user } = useAuth()
    const [searchParams] = useSearchParams()
    const dataLimiteParam = searchParams.get('data') ?? undefined
    const [faturas, setFaturas] = useState<Fatura[]>([])
    const [dataLimite, setDataLimite] = useState<string>()
    const [saldoDisponivel, setSaldoDisponivel] = useState<number | null>(null)
    const [temExtratoSaldo, setTemExtratoSaldo] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const clienteId = user?.CdCliente

    const loadFaturas = useCallback(async () => {
        if (!clienteId) return

        setIsLoading(true)
        try {
            const [{ faturas, dataLimite }, saldo] = await Promise.all([
                getFaturasAbertas(dataLimiteParam),
                getSaldoAntecipado().catch(() => null),
            ])
            setFaturas(faturas)
            setDataLimite(dataLimite)
            if (saldo) {
                setSaldoDisponivel(saldo.disponivel)
                setTemExtratoSaldo(saldo.extrato.length > 0)
            } else {
                setSaldoDisponivel(null)
                setTemExtratoSaldo(false)
            }
        } catch (error) {
            setError(error as Error)
        } finally {
            setIsLoading(false)
        }
    }, [clienteId, dataLimiteParam])

    useEffect(() => {
        loadFaturas()
    }, [loadFaturas])

    if (error) return <ErrorHandler error={error} />
    if (!user) return <Loading type="primary" />

    const mostrarSaldo =
        saldoDisponivel !== null && (saldoDisponivel > 0 || temExtratoSaldo)

    return (
        <div>
            {empresaURL && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Link className="btn btn-outline-primary btn-sm" to={`/${empresaURL}/faturas/pagas`}>
                        {t('Faturas pagas')}
                    </Link>
                </div>
            )}

            {mostrarSaldo && empresaURL ? (
                <SaldoAntecipadoResumoCard
                    disponivel={saldoDisponivel}
                    to={`/${empresaURL}/faturas/saldo-antecipado`}
                />
            ) : null}

            <FaturasView
                faturas={faturas}
                isLoading={isLoading}
                onRefresh={loadFaturas}
                dataLimite={dataLimite}
            />
        </div>
    )
}
